import React, { useEffect, useState } from 'react';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { Alert, confirmAction, showError, showSuccess, showWarning } from '../lib/alerts';

interface WorkspacesPageProps {
  session: Session | null;
  supabase: SupabaseClient;
}

interface WorkspaceData {
  id: string;
  name: string;
  role: string;
  projects: ProjectData[];
  inviteCode?: string;
}

interface ProjectData {
  id: string;
  name: string;
  role: string;
}

export default function WorkspacesPage({ session, supabase }: WorkspacesPageProps) {
  const [workspaces, setWorkspaces] = useState<WorkspaceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);

  async function loadData() {
    if (!session) return;
    try {
      setLoading(true);

      // 1. Fetch memberships first
      const { data: memData, error: memError } = await supabase
        .from('project_members')
        .select('workspace_id, project_id, role')
        .eq('user_id', session.user.id);

      if (memError) throw memError;
      if (!memData || memData.length === 0) {
        setWorkspaces([]);
        return;
      }

      const workspaceIds = Array.from(new Set(memData.map((m) => m.workspace_id).filter(Boolean)));
      const projectIds = Array.from(new Set(memData.map((m) => m.project_id).filter(Boolean)));

      // 2. Fetch workspaces, projects, and invites in parallel
      const [wsRes, projRes, invRes] = await Promise.all([
        supabase.from('workspaces').select('id, name').in('id', workspaceIds),
        supabase.from('projects').select('id, name, workspace_id').in('id', projectIds),
        supabase.from('workspace_invites').select('workspace_id, invite_code').in('workspace_id', workspaceIds).eq('is_active', true),
      ]);

      if (wsRes.error || projRes.error || invRes.error) {
        setDebugError(`WS: ${wsRes.error?.message} | PROJ: ${projRes.error?.message} | INV: ${invRes.error?.message}`);
      }

      const wsData = wsRes.data || [];
      const projData = projRes.data || [];
      const invData = invRes.data || [];

      // Calculate workspace roles (highest role across all projects in the workspace)
      const roleRank = (role: string) => (role === 'owner' ? 3 : role === 'admin' ? 2 : 1);
      
      const workspaceRoles = new Map<string, string>();
      const projectRoles = new Map<string, string>();
      
      for (const m of memData) {
        projectRoles.set(m.project_id, m.role);
        const existingRole = workspaceRoles.get(m.workspace_id);
        const nextRole = existingRole && roleRank(existingRole) >= roleRank(m.role) ? existingRole : m.role;
        workspaceRoles.set(m.workspace_id, nextRole);
      }

      const inviteMap = new Map<string, string>();
      for (const inv of invData || []) {
        inviteMap.set(inv.workspace_id, inv.invite_code);
      }

      const wsNameMap = new Map<string, string>();
      for (const ws of wsData) {
        wsNameMap.set(ws.id, ws.name);
      }

      const formattedData: WorkspaceData[] = workspaceIds.map((wsId) => {
        const wsProjects = projData.filter((p) => p.workspace_id === wsId).map(p => ({
          id: p.id,
          name: p.name,
          role: projectRoles.get(p.id) || 'member'
        }));
        
        return {
          id: wsId,
          name: wsNameMap.get(wsId) || 'Workspace',
          role: workspaceRoles.get(wsId) || 'member',
          projects: wsProjects,
          inviteCode: inviteMap.get(wsId)
        };
      });

      setWorkspaces(formattedData);
    } catch (err) {
      showError('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, supabase]);

  function createSlug(value: string) {
    const base = value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32);
    const suffix = Math.random().toString(36).slice(2, 8);
    return `${base || 'item'}-${suffix}`;
  }

  async function handleCreateWorkspace() {
    const { value: formValues } = await Alert.fire({
      title: 'Create New Workspace',
      html:
        '<input id="swal-input1" class="swal2-input" placeholder="Workspace Name">' +
        '<input id="swal-input2" class="swal2-input" placeholder="Initial Project Name">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Create',
      preConfirm: () => {
        return [
          (document.getElementById('swal-input1') as HTMLInputElement).value,
          (document.getElementById('swal-input2') as HTMLInputElement).value
        ]
      }
    });

    if (!formValues) return;
    const [workspaceName, projectName] = formValues;

    if (!workspaceName || !projectName) {
      showWarning('Both Workspace and Project names are required.');
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('create_workspace', {
        workspace_name: workspaceName,
        workspace_slug: createSlug(workspaceName),
        initial_project_name: projectName,
        initial_project_slug: createSlug(projectName),
      });

      if (error) throw error;
      showSuccess('Workspace created successfully');
      await loadData();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCreateProject(workspaceId: string) {
    const { value: projectName } = await Alert.fire({
      title: 'Create New Project',
      input: 'text',
      inputPlaceholder: 'Project Name',
      showCancelButton: true,
      confirmButtonText: 'Create',
    });

    if (!projectName) return;

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('create_project', {
        p_workspace_id: workspaceId,
        p_project_name: projectName,
        p_project_slug: createSlug(projectName),
      });

      if (error) throw error;
      showSuccess('Project created successfully');
      await loadData();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteProject(projectId: string, projectName: string) {
    const result = await confirmAction('Delete Project?', `Are you sure you want to delete "${projectName}"? This action cannot be undone.`);
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('delete_project', { p_project_id: projectId });
      if (error) throw error;
      showSuccess('Project deleted');
      await loadData();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteWorkspace(workspaceId: string, workspaceName: string) {
    const result = await confirmAction('Delete Workspace?', `Are you sure you want to delete the entire "${workspaceName}" workspace? This will delete ALL projects, members, and data within it. This action CANNOT be undone.`);
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('delete_workspace', { p_workspace_id: workspaceId });
      if (error) throw error;
      showSuccess('Workspace deleted');
      await loadData();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete workspace');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div>
      <main className="dashboard-content">
        <section className="stack-block">
          <div className="section-heading-row section-heading-row--spread">
            <div>
              <div className="section-kicker">MANAGEMENT</div>
              <h2>Workspaces & Projects</h2>
            </div>
            <button 
              className="button button--primary" 
              onClick={handleCreateWorkspace}
              disabled={loading || actionLoading}
            >
              + New Workspace
            </button>
          </div>

          {debugError && (
            <div style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', borderRadius: '8px' }}>
              <strong>Debug Error:</strong> {debugError}
            </div>
          )}

          {loading ? (
            <div style={{ color: 'var(--text-muted)' }}>Loading workspaces...</div>
          ) : workspaces.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', backgroundColor: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>No Workspaces Found</h3>
              <p style={{ color: 'var(--text-muted)' }}>You aren't a member of any workspaces yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {workspaces.map(ws => (
                <div key={ws.id} style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {ws.name}
                        <span className="role-pill">{ws.role}</span>
                      </h3>
                      {ws.inviteCode && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          Team Invite Code: <strong style={{ color: 'var(--text-secondary)' }}>{ws.inviteCode}</strong>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      {['owner', 'admin'].includes(ws.role) && (
                        <button className="button button--ghost" onClick={() => handleCreateProject(ws.id)} disabled={actionLoading}>
                          + Add Project
                        </button>
                      )}
                      {ws.role === 'owner' && (
                        <button className="button button--ghost button--danger" onClick={() => handleDeleteWorkspace(ws.id, ws.name)} disabled={actionLoading}>
                          Delete Workspace
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ padding: '1.5rem' }}>
                    <h4 style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Projects</h4>
                    {ws.projects.length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No projects in this workspace.</div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                        {ws.projects.map(p => (
                          <div key={p.id} style={{ padding: '1rem', backgroundColor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Role: {p.role}</div>
                            </div>
                            {['owner', 'admin'].includes(p.role) && (
                              <button 
                                className="button button--ghost button--danger" 
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                onClick={() => handleDeleteProject(p.id, p.name)}
                                disabled={actionLoading}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
